
import React from 'react';
import Button from '../../../components/base/Button';

const ServicesSection: React.FC = () => {
  return (
    <>
      {/* Structured Data for Services */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Business Acquisition Strategy Services",
            "provider": {
              "@type": "Person",
              "name": "Glory Odudu",
              "jobTitle": "Business Acquisition Strategist",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Sydney",
                "addressRegion": "NSW",
                "addressCountry": "AU"
              }
            },
            "serviceType": "Business Acquisition Consulting",
            "description": "Professional business acquisition, scaling, and exit strategy services for professionals, business owners, and investors in Australia",
            "areaServed": "Australia",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Business Acquisition Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Business Acquisition for Professionals",
                    "description": "Help professionals transition from employees to business owners through strategic acquisitions"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Business Scaling and Exit Strategy",
                    "description": "Strategic business growth through acquisitions and premium exit preparation"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Investment Portfolio Advisory",
                    "description": "Curated off-market business opportunities and portfolio advisory services"
                  }
                }
              ]
            }
          })
        }}
      />

      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6 font-serif">
              Who I <span className="text-yellow-600 italic">Work With</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              <strong>Business acquisition strategist</strong> providing tailored strategies for professionals, business owners, and investors seeking growth through strategic acquisitions across Australia.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" role="main">
            {/* For Professionals Service */}
            <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Business acquisition services for professionals in Australia - Glory Odudu strategist" 
                  title="Professional Business Acquisition Services Australia"
                  className="w-full h-full object-cover object-top" 
                  src="https://public.readdy.ai/ai/img_res/1a9e3629fe9ef0f79b74d37dd8b13bcc.jpg"
                />
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center">
                  <i className="ri-briefcase-line text-black text-xl" aria-hidden="true"></i>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-black mb-2 font-serif">For Professionals</h3>
                <h4 className="text-lg font-semibold text-yellow-600 mb-4">
                  <a href="#contact" title="Buy a business without starting from scratch">
                    Buy a Business Without Starting From Scratch.
                  </a>
                </h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Turn your corporate skills into CEO-level ownership opportunities through <strong>strategic business acquisition</strong> in Australia.
                </p>
                <ul className="space-y-3 mb-6" role="list">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Deal sourcing</strong> & evaluation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Creative finance</strong> structures</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Negotiation</strong> & due diligence</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm">90-day <strong>transition mentorship</strong></span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-sm font-medium text-gray-800">
                    <strong>Result:</strong> Acquire a profitable business that generates wealth through ownership, not just employment.
                  </p>
                </div>
                <Button variant="primary" size="sm" className="w-full">
                  Book a free call now
                </Button>
              </div>
            </article>

            {/* For Business Owners Service */}
            <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Business scaling and exit strategy services Australia - Glory Odudu consultant" 
                  title="Business Scaling Exit Strategy Australia"
                  className="w-full h-full object-cover object-top" 
                  src="https://readdy.ai/api/search-image?query=Business%20owner%20reviewing%20growth%20charts%20and%20acquisition%20strategies%20in%20modern%20boardroom%2C%20professional%20meeting%20with%20financial%20documents%2C%20strategic%20planning%20session%2C%20clean%20corporate%20environment%20with%20presentation%20materials&width=500&height=400&seq=owners-service&orientation=landscape"
                />
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center">
                  <i className="ri-line-chart-line text-black text-xl" aria-hidden="true"></i>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-black mb-2 font-serif">For Business Owners</h3>
                <h4 className="text-lg font-semibold text-yellow-600 mb-4">
                  <a href="#contact" title="Scale or exit business strategically">
                    Scale or Exit Strategically.
                  </a>
                </h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  We help you expand through <strong>strategic acquisitions</strong> or prepare your business for a premium exit in the Australian market.
                </p>
                <ul className="space-y-3 mb-6" role="list">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Growth-by-acquisition</strong> planning</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Business valuation</strong> & investor packaging</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Exit readiness</strong> & buyer introductions</span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-sm font-medium text-gray-800">
                    <strong>Result:</strong> Scale your business portfolio or achieve premium exit valuations through strategic positioning.
                  </p>
                </div>
                <Button variant="primary" size="sm" className="w-full">
                  Book a free call now
                </Button>
              </div>
            </article>

            {/* For Investors Service */}
            <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Investment portfolio advisory services Australia - Glory Odudu business deals" 
                  title="Investment Portfolio Advisory Australia"
                  className="w-full h-full object-cover object-top" 
                  src="https://readdy.ai/api/search-image?query=Investment%20portfolio%20review%20meeting%20with%20financial%20charts%20and%20business%20opportunity%20documents%2C%20professional%20investors%20analyzing%20deals%2C%20modern%20conference%20room%20with%20presentation%20screens%2C%20sophisticated%20business%20environment&width=500&height=400&seq=investors-service&orientation=landscape"
                />
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4 w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-black text-xl" aria-hidden="true"></i>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-black mb-2 font-serif">For Investors</h3>
                <h4 className="text-lg font-semibold text-yellow-600 mb-4">
                  <a href="#contact" title="Build cash-flowing business portfolio">
                    Build a Cash-Flowing Portfolio.
                  </a>
                </h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Access curated, <strong>off-market business opportunities</strong> tailored to your financial goals across Australia.
                </p>
                <ul className="space-y-3 mb-6" role="list">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Deal flow</strong> access</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Partnership</strong> matching</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 mr-3">
                      <i className="ri-check-line text-yellow-600 text-sm" aria-hidden="true"></i>
                    </div>
                    <span className="text-gray-700 text-sm"><strong>Portfolio</strong> advisory</span>
                  </li>
                </ul>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-sm font-medium text-gray-800">
                    <strong>Result:</strong> Build a diversified portfolio of cash-flowing businesses with professional guidance.
                  </p>
                </div>
                <Button variant="primary" size="sm" className="w-full">
                  Book a free call now
                </Button>
              </div>
            </article>
          </div>

          <div className="text-center mt-16">
            <div className="bg-black p-8 rounded-lg">
              <h3 className="text-3xl font-bold text-white mb-4 font-serif">
                Ready to Transform Your <strong className="text-yellow-600">Business Journey</strong>?
              </h3>
              <p className="text-gray-200 mb-6 max-w-2xl mx-auto">
                Whether you're looking to <strong>acquire</strong>, <strong>scale</strong>, or <strong>exit</strong>, let's discuss your strategic options with Australia's leading business acquisition strategist.
              </p>
              <Button variant="primary" size="lg" className="px-8 py-4 text-lg">
                Schedule Your Strategy Session
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;
